/**
 * Typed API client for the admin SPA.
 *
 * Wraps `fetch` with the conventions the OttoPilot API expects:
 *
 *   - `credentials: 'include'` so the `op_session` HttpOnly cookie travels
 *     with every request. The session cookie is the auth signal — no token
 *     is ever decoded on the client (see `useMe()` for the gating pattern).
 *
 *   - `X-CSRF-Token` header is attached automatically on mutation methods
 *     (POST/PUT/PATCH/DELETE) by reading the non-HttpOnly `op_csrf` cookie
 *     (`api/src/auth/cookies.ts`). Mutations without a CSRF cookie surface
 *     a `CSRF_MISSING` client error so the SPA can prompt the operator to
 *     log in again instead of silently failing on the server.
 *
 *   - Response envelope mapping. The API responds with
 *     `{ data, meta? }` on success and `{ error: { code, message, details? } }`
 *     on failure (`standards/api-patterns.md`). This client extracts `data`
 *     on success and throws `ApiClientError` carrying the full envelope on
 *     failure so components can branch on `error.code` without parsing JSON
 *     themselves.
 *
 * Why a custom error class (vs. throwing a plain Response):
 *   - Components/hooks need access to `code`, `message`, `details`, AND the
 *     HTTP status. `ApiClientError` carries all four in one shape.
 *   - TanStack Query's `error` is typed as `unknown` by default; promoting
 *     to a known class makes the `instanceof ApiClientError` narrowing the
 *     idiomatic check at the call site.
 */

import type { ApiErr } from "@ottopilot/shared";

/** Mutation HTTP methods that require the CSRF header. */
const MUTATION_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"]);

/** Cookie name read by the SPA to echo the CSRF token in the header. */
export const CSRF_COOKIE_NAME = "op_csrf";

/** Header name accepted by the API for the double-submit token. */
export const CSRF_HEADER_NAME = "X-CSRF-Token";

/**
 * Successful API envelope. Re-declared here to avoid pulling the broader
 * `ApiResult<T>` discriminated union into surfaces that already know the
 * call succeeded — the client returns the unwrapped `data` directly.
 */
export type ApiSuccessEnvelope<T> = {
  data: T;
  meta?: { page: number; pageSize: number; total: number };
};

/** Page metadata returned alongside paginated lists. */
export type PageMeta = { page: number; pageSize: number; total: number };

/**
 * Typed error thrown for any non-2xx response. Carries the API envelope's
 * `error.code` (machine-readable) and `error.message` (human-facing) plus
 * the HTTP status. Optional `details` mirrors the API's Zod flatten output
 * for field-level surfacing in forms.
 */
export class ApiClientError extends Error {
  public readonly status: number;
  public readonly code: string;
  public readonly details: unknown;

  constructor(opts: { status: number; code: string; message: string; details?: unknown }) {
    super(opts.message);
    this.name = "ApiClientError";
    this.status = opts.status;
    this.code = opts.code;
    this.details = opts.details;
  }
}

/**
 * Read a cookie value by name from `document.cookie`. Returns `null` when
 * the cookie is absent or the document is unavailable (SSR / tests without
 * jsdom). Cookie values are URI-decoded so percent-encoded tokens round-
 * trip correctly.
 */
export function readCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const raw = document.cookie;
  if (!raw) return null;
  const target = `${name}=`;
  for (const part of raw.split("; ")) {
    if (part.startsWith(target)) {
      return decodeURIComponent(part.slice(target.length));
    }
  }
  return null;
}

/**
 * Extract a useful error envelope out of an unsuccessful Response. We try
 * JSON first (the API always serialises errors as JSON), falling back to a
 * generic envelope for non-JSON 5xx pages. Never throws — the caller
 * relies on this helper to deliver an `ApiClientError` it can present.
 */
async function parseErrorEnvelope(res: Response): Promise<ApiErr["error"]> {
  try {
    const body = (await res.json()) as Partial<ApiErr> | null;
    if (body && typeof body === "object" && body.error && typeof body.error === "object") {
      const err = body.error;
      return {
        code: typeof err.code === "string" ? err.code : "UNKNOWN_ERROR",
        message: typeof err.message === "string" ? err.message : res.statusText || "Request failed",
        details: err.details,
      };
    }
  } catch {
    // Non-JSON body — fall through to generic envelope.
  }
  return {
    code: res.status >= 500 ? "SERVER_ERROR" : "UNKNOWN_ERROR",
    message: res.statusText || "Request failed",
  };
}

export type RequestOptions = {
  /** HTTP method. Defaults to GET. */
  method?: string;
  /** Optional JSON body — serialised with `JSON.stringify`. */
  body?: unknown;
  /** Optional query-string params. Skipped when the value is `undefined`. */
  query?: Record<string, string | number | boolean | undefined>;
  /** Extra headers — merged after the client-managed defaults. */
  headers?: Record<string, string>;
  /**
   * Allows overriding `fetch` (test harnesses). Defaults to global `fetch`.
   */
  fetchImpl?: typeof fetch;
  /**
   * Optional reader for the CSRF cookie. Defaults to `readCookie`. Tests
   * inject a stub to avoid wrangling `document.cookie`.
   */
  csrfCookieReader?: (name: string) => string | null;
};

function buildUrl(path: string, query?: RequestOptions["query"]): string {
  if (!query) return path;
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined) continue;
    search.append(key, String(value));
  }
  const qs = search.toString();
  return qs.length > 0 ? `${path}?${qs}` : path;
}

/**
 * Low-level request primitive. Most callers use the named helpers below
 * (`apiGet`, `apiPost`, ...) which thread the right `method`. Exported so
 * tests and one-off callers can pass arbitrary methods.
 */
export async function apiRequest<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const method = (opts.method ?? "GET").toUpperCase();
  const fetchImpl = opts.fetchImpl ?? fetch;
  const cookieReader = opts.csrfCookieReader ?? readCookie;

  const headers: Record<string, string> = {
    Accept: "application/json",
    ...(opts.headers ?? {}),
  };

  const init: RequestInit = {
    method,
    credentials: "include",
    headers,
  };

  if (opts.body !== undefined) {
    headers["Content-Type"] = "application/json";
    init.body = JSON.stringify(opts.body);
  }

  if (MUTATION_METHODS.has(method)) {
    const csrf = cookieReader(CSRF_COOKIE_NAME);
    if (csrf) {
      headers[CSRF_HEADER_NAME] = csrf;
    }
    // We deliberately do NOT short-circuit when the cookie is missing —
    // the server will reject with `CSRF_INVALID` and the SPA will surface
    // the same 403 it would for any forged-token case. Pre-checking here
    // would diverge from the server's view of the world (e.g. when login
    // succeeded in another tab and this tab hasn't re-read cookies yet).
  }

  const url = buildUrl(path, opts.query);

  let res: Response;
  try {
    res = await fetchImpl(url, init);
  } catch (err) {
    // Network error — fetch rejects only for true network failures (DNS,
    // CORS, offline). HTTP errors are NOT thrown — they reach the !ok
    // branch below.
    const message = err instanceof Error ? err.message : "Network error";
    throw new ApiClientError({
      status: 0,
      code: "NETWORK_ERROR",
      message,
    });
  }

  if (res.status === 204) {
    // No content — return undefined. Callers that expect `void` get the
    // shape they want; callers that expect `T` should not use the helper
    // for 204 endpoints.
    return undefined as T;
  }

  if (!res.ok) {
    const envelope = await parseErrorEnvelope(res);
    throw new ApiClientError({
      status: res.status,
      code: envelope.code,
      message: envelope.message,
      details: envelope.details,
    });
  }

  let body: unknown;
  try {
    body = await res.json();
  } catch {
    throw new ApiClientError({
      status: res.status,
      code: "INVALID_JSON",
      message: "Server returned invalid JSON.",
    });
  }

  if (!body || typeof body !== "object" || !("data" in body)) {
    throw new ApiClientError({
      status: res.status,
      code: "INVALID_ENVELOPE",
      message: "Server response did not contain a `data` field.",
    });
  }
  return (body as ApiSuccessEnvelope<T>).data;
}

/**
 * Variant of `apiRequest` that returns both the unwrapped `data` and the
 * pagination `meta` block. Used by list endpoints that paginate.
 */
export async function apiList<T>(
  path: string,
  opts: RequestOptions = {},
): Promise<{ data: T; meta: PageMeta | undefined }> {
  const method = (opts.method ?? "GET").toUpperCase();
  const fetchImpl = opts.fetchImpl ?? fetch;
  const cookieReader = opts.csrfCookieReader ?? readCookie;

  const headers: Record<string, string> = {
    Accept: "application/json",
    ...(opts.headers ?? {}),
  };

  const init: RequestInit = { method, credentials: "include", headers };

  if (opts.body !== undefined) {
    headers["Content-Type"] = "application/json";
    init.body = JSON.stringify(opts.body);
  }

  if (MUTATION_METHODS.has(method)) {
    const csrf = cookieReader(CSRF_COOKIE_NAME);
    if (csrf) headers[CSRF_HEADER_NAME] = csrf;
  }

  const url = buildUrl(path, opts.query);

  let res: Response;
  try {
    res = await fetchImpl(url, init);
  } catch (err) {
    const message = err instanceof Error ? err.message : "Network error";
    throw new ApiClientError({ status: 0, code: "NETWORK_ERROR", message });
  }

  if (!res.ok) {
    const envelope = await parseErrorEnvelope(res);
    throw new ApiClientError({
      status: res.status,
      code: envelope.code,
      message: envelope.message,
      details: envelope.details,
    });
  }

  const body = (await res.json()) as ApiSuccessEnvelope<T>;
  return { data: body.data, meta: body.meta };
}

/** Convenience helpers for the common methods. */
export function apiGet<T>(path: string, opts: Omit<RequestOptions, "method" | "body"> = {}) {
  return apiRequest<T>(path, { ...opts, method: "GET" });
}
export function apiPost<T>(
  path: string,
  body?: unknown,
  opts: Omit<RequestOptions, "method"> = {},
) {
  return apiRequest<T>(path, { ...opts, method: "POST", body });
}
export function apiPatch<T>(
  path: string,
  body?: unknown,
  opts: Omit<RequestOptions, "method"> = {},
) {
  return apiRequest<T>(path, { ...opts, method: "PATCH", body });
}
export function apiDelete<T>(path: string, opts: Omit<RequestOptions, "method" | "body"> = {}) {
  return apiRequest<T>(path, { ...opts, method: "DELETE" });
}
